/** Frontend reader for a workspace's real subscription state.
 *
 * companies.billing_tier records what a workspace last signed up for, not
 * whether they are still paying, and it is CHECKed against the four paid tiers
 * so it has no value meaning "not subscribed". Screens reading it alone present
 * a lapsed customer as a paying one.
 *
 * v_company_subscription_state derives the answer from paid invoice periods
 * instead. It is security_invoker over RLS-gated tables, so a workspace member
 * sees only their own row and internal staff see everything - no server route
 * is needed.
 */

import { getSupabaseClient } from '../utils/supabase/client';

import type { CompanySubscriptionState, SubscriptionState } from '../types/subscriptionState';
export type { CompanySubscriptionState, SubscriptionState } from '../types/subscriptionState';

const COLUMNS =
  'company_id, tier_on_record, state, paid_tier, credits, is_unlimited, paid_until';

/** Batch lookup for list screens. Returns a map keyed by company id; companies
 *  the caller cannot see are simply absent rather than erroring. */
export async function fetchSubscriptionStates(
  companyIds: string[],
): Promise<Record<string, CompanySubscriptionState>> {
  if (companyIds.length === 0) return {};

  // Cast: src/types/generated/database.ts is generated and does not yet know
  // this view. Same pattern as the period_monthly_credits RPC call sites.
  const { data, error } = await (getSupabaseClient() as any)
    .from('v_company_subscription_state')
    .select(COLUMNS)
    .in('company_id', companyIds);

  if (error) {
    // Never fatal: a screen without this data falls back to the tier on record,
    // which is the pre-existing behaviour.
    console.warn('[SubscriptionStateService] batch load failed:', error.message);
    return {};
  }

  const map: Record<string, CompanySubscriptionState> = {};
  (data || []).forEach((row: any) => {
    map[row.company_id] = row as CompanySubscriptionState;
  });
  return map;
}

export async function fetchSubscriptionState(
  companyId: string,
): Promise<CompanySubscriptionState | null> {
  if (!companyId) return null;

  // Cast: see fetchSubscriptionStates - the view is absent from the generated types.
  const { data, error } = await (getSupabaseClient() as any)
    .from('v_company_subscription_state')
    .select(COLUMNS)
    .eq('company_id', companyId)
    .maybeSingle();

  if (error) {
    console.warn('[SubscriptionStateService] load failed:', error.message);
    return null;
  }
  return (data as CompanySubscriptionState | null) ?? null;
}

/** Short label for a state badge. Returns null when there is nothing worth
 *  saying - an active subscription needs no annotation. */
export function subscriptionStateLabel(
  state: SubscriptionState | undefined,
): string | null {
  if (state === 'lapsed') return 'Lapsed';
  if (state === 'unknown') return 'Unverified';
  return null;
}
